import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import { OUTREACH_PITCH_KINDS } from 'fym-shared/src/music24/outreach/dailyPitches';
import { TypedMemo } from '../../../common/types';
import type { PeopleFilter } from '../hooks/useCampaignPeople';
import type { PeopleResult } from '../outreachTypes';
import { isPitchKind, pitchKindGroupName } from './pitchKindCopy';
import { peopleMessages as messages } from './peopleMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import campaign from './Campaign.module.scss';
import styles from './people/People.module.scss';
/* eslint-enable css-modules/no-unused-class */

const SEGMENTS: Array<{ key: PeopleResult | null; label: MessageDescriptor }> = [
  { key: null, label: messages.filterAll },
  { key: 'scheduled', label: messages.filterPlanned },
  { key: 'opened', label: messages.filterOpened },
  { key: 'listened', label: messages.filterListened },
  { key: 'replied', label: messages.filterReplied },
  { key: 'added', label: messages.filterAdded },
];

/**
 * The filters of the People list (design outreach-v2 t1): the result as tabs (All, Planned,
 * Opened, Listened, Replied, Added) and the kind as a menu.
 */
const PeopleFiltersComponent: React.FC<{ filter: PeopleFilter; onChange: (filter: PeopleFilter) => void }> = ({
  filter,
  onChange,
}) => {
  const intl = useIntl();
  const kinds = useMemo(
    () =>
      OUTREACH_PITCH_KINDS.map((kind) => (
        <option key={kind} value={kind}>
          {pitchKindGroupName(intl, kind)}
        </option>
      )),
    [intl]
  );
  const segments = useMemo(
    () =>
      SEGMENTS.map((segment) => (
        <button
          key={segment.key ?? 'all'}
          type="button"
          role="tab"
          aria-selected={filter.result === segment.key}
          className={clsx(styles.segment, filter.result === segment.key && styles.segmentOn)}
          onClick={() => onChange({ ...filter, result: segment.key })}
          data-testid={`people-filter-result-${segment.key ?? 'all'}`}
        >
          {intl.formatMessage(segment.label)}
        </button>
      )),
    [filter, intl, onChange]
  );
  return (
    <div className={styles.tools}>
      <div className={styles.segments} role="tablist" aria-label={intl.formatMessage(messages.resultFilter)}>
        {segments}
      </div>
      <select
        className={campaign.select}
        aria-label={intl.formatMessage(messages.kindFilter)}
        value={filter.kind ?? ''}
        onChange={(event) => onChange({ ...filter, kind: isPitchKind(event.target.value) ? event.target.value : null })}
        data-testid="people-filter-kind"
      >
        <option value="">{intl.formatMessage(messages.allKinds)}</option>
        {kinds}
      </select>
    </div>
  );
};

export const PeopleFilters = TypedMemo(PeopleFiltersComponent);
